import { createContext, type ReactNode, type Ref } from "react";
import { BRAND } from "../brand.ts";
import { sharedConsent } from "../consent.ts";
import { useSources } from "../data/sources.ts";
import { useBalance } from "../data/useBalance.ts";
import { usePlanInfo } from "../data/usePlan.ts";
import type { ProductionState } from "../data/useSteps.ts";
import { docsUrl } from "../docs.ts";
import { atLeast } from "../lib.ts";
import type { Project, Workspace } from "../types.ts";
import { MOD, useApp } from "./app.ts";
import { Avatar } from "./collab/Avatars.tsx";
import { TopBarCollab } from "./collab/TopBarCollab.tsx";
import { Icon, type IconName, Mark } from "./icons.tsx";
import { meterLine } from "./kit.tsx";
import { Menu } from "./Menu.tsx";
import { SideProductions } from "./SideProductions.tsx";

export interface Me {
  /** the user id */
  id?: string | null;
  email?: string | null;
  name?: string | null;
}

/**
 * Where a production's folder tree goes on a wide screen: an element under that production in the sidebar.
 * Null on a phone, where the tree stays a drawer of its own (ProjectView renders it in place then).
 */
export const SideSlot = createContext<HTMLElement | null>(null);

/** Where a screen puts its own actions in the top bar (Claude, Share). Null on a phone: they stay in the screen. */
export const TopSlot = createContext<HTMLElement | null>(null);

interface WorkspaceProps {
  workspaces: Workspace[];
  ws: string;
  onWorkspace: (id: string) => void;
}

interface AccountProps {
  me: Me | null;
  onSignOut: () => void;
}

const initial = (name: string | undefined) => (name?.trim()[0] ?? "?").toUpperCase();

/**
 * The workspace switcher: the workspace's avatar, its name and a chevron. In the sidebar it sits next to the logo
 * (as in Linear) and is the one menu there: it also holds the account (`account`). On a phone it sits next to the
 * logo in the top bar, boxed, with the account in a menu of its own. The menu switches workspace and holds the
 * workspace's own pages.
 */
function WorkspaceMenu({
  workspaces,
  ws,
  onWorkspace,
  top,
  account,
}: WorkspaceProps & {
  top?: boolean;
  /** the sidebar's menu: the account's items too; `rail` puts the balance in it (the meter is hidden then) */
  account?: AccountProps & { rail?: boolean };
}) {
  const { role, go } = useApp();
  const current = workspaces.find((w) => w.id === ws);
  return (
    <Menu
      label={account ? "Workspace and account" : "Workspace"}
      buttonClass={top ? "side-ws top-ws" : "side-ws"}
      button={
        <>
          <span className="ws-av" aria-hidden="true">
            {initial(current?.name)}
          </span>
          <span className="ws-name">{current?.name ?? "Workspace"}</span>
          <Icon name="chevron" size={12} className="down" />
        </>
      }
    >
      {(close) => (
        <>
          {account && (
            <>
              <div className="menu-note">{account.me?.email ?? "Signed in with an API key"}</div>
              {account.rail && <MenuBalance close={close} />}
              <hr className="menu-sep" />
            </>
          )}
          <div className="menu-label">Workspaces</div>
          {workspaces.map((w) => (
            <button
              key={w.id}
              type="button"
              role="menuitemradio"
              aria-checked={w.id === ws}
              onClick={() => {
                onWorkspace(w.id);
                close();
              }}
            >
              <span className="ws-av" aria-hidden="true">
                {initial(w.name)}
              </span>
              <span className="grow">{w.name}</span>
              <span className="muted small">{w.role}</span>
              {w.id === ws && <Icon name="check" size={12} />}
            </button>
          ))}
          <hr className="menu-sep" />
          {atLeast(role, "producer") && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                go({ view: "settings", page: "members" });
              }}
            >
              Members and access
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              go({ view: "credits" });
            }}
          >
            Credits and plan
          </button>
          {account && (
            <>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  go({ view: "settings", page: "claude" });
                }}
              >
                Your Claude
              </button>
              <hr className="menu-sep" />
              {BRAND.analytics && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    sharedConsent().reopen();
                  }}
                >
                  Cookie settings
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  account.onSignOut();
                }}
              >
                Sign out
              </button>
            </>
          )}
        </>
      )}
    </Menu>
  );
}

/** The accessible name of every way into the docs: it says where the link goes and that it leaves the studio. */
export const DOCS_LABEL = "Docs for this screen (opens in a new tab)";

/** The docs link of a screen: the quiet row at the foot of the sidebar (on a phone it is in the account menu, same words). */
function DocsLink({ rail }: { rail?: boolean }) {
  const { docsHref } = useApp();
  return (
    <a
      className="side-link side-docs"
      href={docsHref ?? docsUrl("")}
      target="_blank"
      rel="noopener"
      aria-label={DOCS_LABEL}
      aria-keyshortcuts="Shift+?"
      title={rail ? "Docs for this screen" : "Docs for this screen (?)"}
    >
      <Icon name="docs" size={15} />
      <span className="grow">Docs for this screen</span>
      <Icon name="external" size={12} className="side-ext" />
    </a>
  );
}

/**
 * The balance on a phone, where there is no sidebar meter: at the top of the account menu, credits and euros in
 * the mono face, with a plain label.
 */
function MenuBalance({ close }: { close: () => void }) {
  const { ws, go } = useApp();
  const { billing } = useSources();
  const b = useBalance(billing, ws, "account-menu");
  const plan = usePlanInfo(billing, ws, "account-menu");
  if (!b) return null;
  const metered = b.billing === "credits";
  // the same words as the sidebar meter: what there is, of what, and the way to more (or to the use)
  const line = meterLine({
    metered,
    credits: b.credits,
    usedCredits: b.usedCredits,
    monthlyCredits: plan?.planCreditsPerMonth,
  });
  return (
    <div className="menu-note menu-balance">
      <span className="k-money">{line.main}</span>
      {line.of && <span className="k-money menu-balance-of">{line.of}</span>}
      <span className="menu-balance-foot">
        <span>{line.label}</span>
        <button
          type="button"
          className="link"
          onClick={() => {
            close();
            go({ view: "credits" });
          }}
        >
          {metered ? (plan?.plan === "free" ? "Get seats" : "Add credits") : "Usage"}
        </button>
      </span>
    </div>
  );
}

/** The account on a phone, in the top bar (in the sidebar the workspace menu holds the account). */
function AccountMenu({ me, onSignOut }: AccountProps) {
  const { role, go, docsHref } = useApp();
  const docs = docsHref ?? docsUrl("");
  const name = me?.name || me?.email || "?";
  return (
    <Menu
      label="Account"
      align="end"
      buttonClass="top-btn"
      menuClass="account-menu"
      button={<Avatar userId={me?.id} name={name} size={24} className="avatar-me" />}
    >
      {(close) => (
        <>
          <div className="menu-note">{me?.email ?? "Signed in with an API key"}</div>
          <MenuBalance close={close} />
          <hr className="menu-sep" />
          {atLeast(role, "producer") && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                go({ view: "settings", page: "members" });
              }}
            >
              Members and access
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              go({ view: "settings", page: "claude" });
            }}
          >
            Your Claude
          </button>
          <a
            role="menuitem"
            className="menu-link"
            href={docs}
            target="_blank"
            rel="noopener"
            aria-label={DOCS_LABEL}
            onClick={close}
          >
            <span className="grow">Docs for this screen</span>
            <Icon name="external" size={12} />
          </a>
          {BRAND.analytics && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                sharedConsent().reopen();
              }}
            >
              Cookie settings
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              onSignOut();
            }}
          >
            Sign out
          </button>
        </>
      )}
    </Menu>
  );
}

/** One step of the breadcrumb; the last one is the screen in view. */
export interface Crumb {
  label: string;
  href?: string;
}

/**
 * The 52px bar over the content (APP-SHELL-v2): breadcrumb, ⌘K search, the people in this production, the bell,
 * and at most one contextual primary action. On a phone there is no sidebar, so the bar also carries the folder
 * toggle, the logo, the workspace and the account; the main nav moves to the bottom tab bar.
 */
export function TopBar({
  workspaces,
  ws,
  onWorkspace,
  me,
  onSignOut,
  onMenu,
  menuOpen,
  where,
  crumbs,
  tabs,
  slotRef,
  action,
}: WorkspaceProps &
  AccountProps & {
    onMenu?: () => void;
    menuOpen?: boolean;
    /** the last breadcrumb: the screen or production in view */
    where?: string;
    /** the whole breadcrumb, when it is more than "workspace / where" */
    crumbs?: Crumb[];
    /** a production's feature tabs: they take the place of the breadcrumb */
    tabs?: ReactNode;
    /** where a screen's own actions go (TopSlot) */
    slotRef?: Ref<HTMLDivElement>;
    /** the screen's one primary action, when it belongs in the bar */
    action?: ReactNode;
  }) {
  const { openPalette, compact } = useApp();
  const current = workspaces.find((w) => w.id === ws);
  const trail: Crumb[] = crumbs ?? [
    { label: current?.name ?? "Workspace", href: "#/" },
    ...(where ? [{ label: where }] : []),
  ];
  return (
    <header className="topbar">
      {onMenu && (
        <button
          type="button"
          className="top-btn nav-toggle"
          aria-label={menuOpen ? "Close folders" : "Open folders"}
          aria-expanded={menuOpen}
          onClick={onMenu}
        >
          <Icon name={menuOpen ? "close" : "menu"} />
        </button>
      )}
      {compact ? (
        <>
          <a className="brand" href="#/" aria-label={`${BRAND.name}, home`}>
            <Mark />
            <span>{BRAND.name}</span>
          </a>
          <WorkspaceMenu workspaces={workspaces} ws={ws} onWorkspace={onWorkspace} top />
        </>
      ) : tabs ? (
        tabs
      ) : (
        <nav className="crumb" aria-label="Breadcrumb">
          <ol>
            {trail.map((c, i) => {
              const last = i === trail.length - 1;
              return (
                <li key={c.href ?? c.label}>
                  {last ? (
                    <span className="crumb-here" aria-current="page">
                      {c.label}
                    </span>
                  ) : c.href ? (
                    <a href={c.href}>{c.label}</a>
                  ) : (
                    <span>{c.label}</span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      )}
      <button
        type="button"
        className="search-btn"
        onClick={openPalette}
        aria-label="Search or jump to"
        aria-keyshortcuts="Meta+K Control+K"
        title={`Search or jump to (${MOD} K)`}
      >
        <Icon name="search" size={14} />
        <span className="search-text">Search or jump to…</span>
        <kbd>{MOD}K</kbd>
      </button>
      <span className="grow" />
      <div className="top-slot" ref={slotRef} />
      <TopBarCollab />
      {action && <div className="top-action">{action}</div>}
      {compact && <AccountMenu me={me} onSignOut={onSignOut} />}
    </header>
  );
}

export type NavId = "home" | "create" | "productions" | "elements" | "explore" | "learn";

export const NAV: readonly { id: NavId; label: string; href: string; icon: IconName; phone: boolean }[] = [
  { id: "home", label: "Home", href: "#/", icon: "home", phone: true },
  { id: "create", label: "Create", href: "#/create", icon: "create", phone: true },
  { id: "productions", label: "Productions", href: "#/productions", icon: "productions", phone: true },
  { id: "elements", label: "Elements", href: "#/elements", icon: "grid", phone: false },
  { id: "explore", label: "Explore", href: "#/explore", icon: "explore", phone: true },
  { id: "learn", label: "Learn", href: "#/learn", icon: "learn", phone: true },
];

/** The element id of the folder tree slot; only the production in view has one. */
const SLOT_ID = "side-slot";

/**
 * The 232px sidebar on wide screens (APP-SHELL-v2): logo with the workspace and account menu next to it, the six
 * main places, your productions in your own order with their nine-square strips (SideProductions: drag to
 * reorder, a ⋯ menu per row; the production in view opens up into its folder tree: `slotRef` is where ProjectView
 * puts it), the docs link and the credits meter.
 */
export function Sidebar({
  workspaces,
  ws,
  onWorkspace,
  me,
  onSignOut,
  nav,
  productions,
  states,
  activeProject,
  slotRef,
  folded,
  onFold,
  onReorder,
  meter,
}: WorkspaceProps &
  AccountProps & {
    /** the main place in view, if any */
    nav: NavId | null;
    /** every production, in your own order */
    productions: readonly Project[];
    states: ReadonlyMap<string, ProductionState>;
    activeProject: string | null;
    slotRef: Ref<HTMLDivElement>;
    /** productions whose folder tree is folded away */
    folded: ReadonlySet<string>;
    /** fold or unfold the folder tree of a production */
    onFold: (id: string) => void;
    /** the production ids in the order you dragged them to */
    onReorder: (ids: readonly string[]) => void;
    meter: ReactNode;
  }) {
  // in Create the sidebar folds to its icons, so the takes get the screen; the name shows on hover
  const rail = nav === "create";
  return (
    <aside className="app-side" aria-label="Workspace">
      <div className="side-top">
        <a className="brand side-brand" href="#/" aria-label={`${BRAND.name}, home`} title={BRAND.name}>
          <Mark />
        </a>
        <WorkspaceMenu
          workspaces={workspaces}
          ws={ws}
          onWorkspace={onWorkspace}
          account={{ me, onSignOut, rail }}
        />
      </div>
      <nav className="side-nav" aria-label="Main">
        {NAV.map((n) => (
          <a
            key={n.id}
            className="side-link"
            href={n.href}
            title={rail ? n.label : undefined}
            aria-current={nav === n.id ? "page" : undefined}
          >
            <Icon name={n.icon} size={15} />
            {n.label}
          </a>
        ))}
      </nav>
      <div className="side-scroll">
        <p className="side-label">Productions</p>
        <SideProductions
          productions={productions}
          states={states}
          activeProject={activeProject}
          slotRef={slotRef}
          slotId={SLOT_ID}
          folded={folded}
          onFold={onFold}
          onReorder={onReorder}
        />
      </div>
      <div className="side-bottom">
        <DocsLink rail={rail} />
        {meter}
      </div>
    </aside>
  );
}

/** The phone's bottom tab bar (APP-SHELL-v2): Home · Create · Productions · Explore · Learn. */
export function BottomTabs({ nav }: { nav: NavId | null }) {
  // Elements has no tab of its own on a phone: it sits under Productions.
  const on = nav === "elements" ? "productions" : nav;
  return (
    <nav className="bottom-tabs" aria-label="Main">
      {NAV.filter((n) => n.phone).map((n) => (
        <a key={n.id} href={n.href} aria-current={on === n.id ? "page" : undefined}>
          <Icon name={n.icon} size={18} />
          <span>{n.label}</span>
        </a>
      ))}
    </nav>
  );
}
